/* eslint-disable react/prop-types */
/**
 * Entry application component used to compose providers and render Routes.
 * */

import { Amplify } from 'aws-amplify'
import { signOut as amplifySignOut, fetchUserAttributes } from 'aws-amplify/auth'
import { Cache } from 'aws-amplify/utils'
import React, { useEffect, useState } from 'react'
import TagManager from 'react-gtm-module'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { PersistGate } from 'redux-persist/integration/react'
import { I18nProvider, LayoutSplashScreen, ThemeProvider } from './_augmentt'
import * as branding from './_augmentt/ducks/branding'
import * as builder from './_augmentt/ducks/builder'
import * as externalLinks from './_augmentt/ducks/externalLinks'
import * as licenses from './_augmentt/ducks/licenses'
import * as meta from './_augmentt/ducks/meta'
import * as user from './_augmentt/ducks/user'
import * as userPermissions from './_augmentt/ducks/userPermissions'
import { useGetFeatureFlagsQuery } from './_augmentt/services/featureFlags'
import { AWSRumContext } from './app/context/AWSRumContext'
import { getExternalLinks } from './app/crud/app.crud'
import { getBranding } from './app/crud/branding.crud'
import { getOrg } from './app/crud/orgs.crud'
import { getUserById } from './app/crud/users.crud'
import useAwsRum from './app/hooks/useAwsRum'
import awsConfig from './app/pages/auth/aws-exports'
import GoogleTagManagerVars from './app/partials/content/GoogleTagManagerVars'
import { Routes } from './app/router/Routes'
import store from './app/store/store'

Amplify.configure(awsConfig)

const GOOGLE_TAG_MANAGER_ID = 'GTM-WK2PZ3B'

let isGtmInitialized = false

function initGoogleTagManager() {
  if (isGtmInitialized || typeof window === 'undefined') {
    return
  }

  TagManager.initialize({ gtmId: GOOGLE_TAG_MANAGER_ID })
  isGtmInitialized = true
}

export default function App({ persistor, basename }) {
  const [isAuthenticating, setIsAuthenticating] = useState(true)

  const dispatch = useDispatch()

  const currentUser = useSelector(({ user }) => user.data) // eslint-disable-line no-shadow
  const license = useSelector(({ licenses }) => licenses.data) // eslint-disable-line no-shadow

  // Force a fresh feature-flag fetch when a user session becomes active.
  // This prevents stale RTK Query cache from a previous login session.
  useGetFeatureFlagsQuery(undefined, {
    skip: !currentUser,
    refetchOnMountOrArgChange: true,
  })

  async function signOut() {
    await amplifySignOut()
    setIsAuthenticating(false)
  }

  // When at 'setup-Mfa' page the user hits on Browser's back button then we need to signOut User.
  // Below function not actually captures the Browser's back button event but rely more on onload event.
  // Whenever there is an onload on the window and something is pushed to history we are capturing that.
  // In this particular case if 'auth/login' is pushed in history from any other page(for now only 'setup-Mfa')
  // then we clear redux store and signOut user.
  // eslint-disable-next-line func-names
  window.onload = function () {
    if (typeof window.history.pushState === 'function') {
      window.history.pushState('NothingPressed', null, null)
      // eslint-disable-next-line func-names
      window.onpopstate = function () {
        if (window.location.pathname === '/auth/login') {
          window.history.pushState('BackButtonPressed', null, null)
          signOut()
        }
      }
    }
  }

  useEffect(() => {
    updateSplashScreen() // eslint-disable-line no-use-before-define
    getUser() // eslint-disable-line no-use-before-define
    initGoogleTagManager()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.body.classList.toggle('cookieyes-hide-revisit', Boolean(currentUser))

    return () => {
      document.body.classList.remove('cookieyes-hide-revisit')
    }
  }, [currentUser])

  useEffect(() => {
    if (window.location.pathname !== '/error') {
      // Get license information & permissions if we don't have them
      if (currentUser && !license) {
        // eslint-disable-next-line no-use-before-define
        getOrganization().then(() => {
          setIsAuthenticating(false)
        })
      }
    }
  }, [currentUser, license]) // eslint-disable-line react-hooks/exhaustive-deps

  // Trigger feature flag pull
  useEffect(() => {
    dispatch(
      builder.actions.setSubHeaderDisplay({
        display: window.location.pathname !== '/welcome' ?? false,
      }),
    )
    loadFeatureFlags() // eslint-disable-line no-use-before-define
  }, [currentUser]) // eslint-disable-line react-hooks/exhaustive-deps

  // Installs AWS CloudWatch RUM snippet
  const awsRum = useAwsRum()

  async function loadFeatureFlags() {
    if (!currentUser) {
      return // we need a user, and we only retrieve the data once from the backend
    }

    try {
      const externalLinksData = await getExternalLinks()

      dispatch(externalLinks.actions.setLinks(externalLinksData.data))
    } catch (e) {
      console.error('Feature flags failed to load. Error:', e)
    }
  }

  async function getUser() {
    const session = Cache.getItem('Session')
    if (!session) {
      // Sign out if session is expired/non-existent
      signOut()
    } else {
      try {
        const attributes = await fetchUserAttributes()
        const email = attributes?.email ?? attributes?.preferred_username
        const userInfo = await getUserById(email)
        const mspBranding = await getBranding(userInfo.data.userData.Customer.Organization.id)

        dispatch(user.actions.setUser(userInfo.data.userData))
        dispatch(
          userPermissions.actions.setUserPermissions({
            customers: userInfo.data.customers,
            permissions: userInfo.data.permissions,
            collectionId: userInfo.data.collectionId,
          }),
        )
        dispatch(userPermissions.actions.setOwnerChargeBeeID(userInfo.data.ownerChargebeeid))
        dispatch(
          branding.actions.setBranding(
            mspBranding.data || {
              logo_light: null,
              publish_logo: false,
              favicon: null,
              publish_favicon: false,
            },
          ),
        )
      } catch (e) {
        console.error(e)
        setIsAuthenticating(false)
      }
    }
  }

  async function getOrganization() {
    // subscription, licenses, modules
    if (currentUser.Customer) {
      const response = await getOrg(currentUser.Customer.Organization.id)
      try {
        const data = response?.data
        dispatch(
          meta.actions.setOrganizationMeta({
            cloudallyUrl: data?.cloudally_url,
            securityTrainingUrl: data?.security_training_url,
            hasValidSubscription: data?.hasValidSubscription,
            chargebeeId: data?.chargebeeId,
            hasValidM365LicensingReportSubscription: data?.hasValidM365LicensingReportSubscription,
            hasValidPostureBaselineSubscription: data?.hasPbSub,
            hasMultiCspSubscription: data?.hasMultiCspSubscription ?? false,
            hasMfaOnlySubscription: data?.hasMfaOnlySubscription ?? false,
            hasEngageAutopilotSubscription: data?.hasEngageAutopilotSubscription ?? false,
            hasIntuneSubscription: data?.hasIntuneSubscription ?? false,
            hasPurviewSubscription: data?.hasPurviewSubscription ?? false,
          }),
        )
        dispatch(licenses.actions.setLicenses(data?.licenses))

        return true
      } catch (e) {
        console.error(e)
        return false
      }
    }
    return false
  }

  const updateSplashScreen = () => {
    const trialLogin = document.getElementById('trial-login-msg')
    const spinner = document.getElementById('spinner')

    if (
      window.location.hostname === 'app.test.augmentt.com' &&
      (window.location.pathname === '/auth/login' || window.location.pathname === '/')
    ) {
      trialLogin.classList.remove('hidden')
      spinner.classList.add('hidden')
    } else {
      trialLogin.classList.add('hidden')
      spinner.classList.remove('hidden')
    }
  }

  return (
    !isAuthenticating && (
      /* Provide Redux store */
      <Provider store={store}>
        {/* Asynchronously persist redux stores and show `SplashScreen` while it's loading. */}
        <PersistGate persistor={persistor} loading={<LayoutSplashScreen />}>
          {/* Add high level `Suspense` in case if was not handled inside the React tree. */}
          <React.Suspense fallback={<LayoutSplashScreen />}>
            {/* Override `basename` (e.g: `homepage` in `package.json`) */}
            <BrowserRouter basename={basename}>
              {/* Provide Metronic theme overrides. */}
              <ThemeProvider>
                {/* Provide `react-intl` context synchronized with Redux state. */}
                <I18nProvider>
                  {/* Provide awsRum library and config to entire application. */}
                  <AWSRumContext.Provider value={awsRum}>
                    {/* Provides ChurnZero access to user variables */}
                    <GoogleTagManagerVars />
                    {/* Render routes with provided `Layout`. */}
                    <Routes />
                  </AWSRumContext.Provider>
                </I18nProvider>
              </ThemeProvider>
            </BrowserRouter>
          </React.Suspense>
        </PersistGate>
      </Provider>
    )
  )
}
